((root, factory) => {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.CorkopiaRuleEngine = api;
})(typeof globalThis !== 'undefined' ? globalThis : window, () => {
  'use strict';

  const RULE_VERSION = '1.0.0';
  const presentMeasurement = (measurement) =>
    Number.isFinite(Number(measurement?.value)) && Number(measurement.value) > 0;
  const variantIdFor = (product) => String(
    product?.commerce?.selectedVariant?.id || product?.identity?.shopifyVariantId || ''
  );
  const evidence = (path, value, source = 'shopify_public') => [{
    path,
    value,
    source,
    classification: 'public'
  }];
  const result = (rule, product, outcome, reasonCode, evidenceItems = []) => ({
    rule_id: rule.id,
    rule_version: RULE_VERSION,
    subject: {
      product_id: String(product?.identity?.shopifyProductId || ''),
      variant_id: variantIdFor(product),
      piece_id: String(product?.identity?.pieceId || '')
    },
    outcome,
    enforcement: rule.enforcement,
    reason_code: reasonCode,
    evidence: evidenceItems,
    explanation_code: reasonCode
  });

  const buildRules = [
    {
      id: 'build.identity.complete',
      enforcement: 'HARD',
      evaluate(product) {
        const complete = Boolean(
          product?.identity?.shopifyProductId
          && variantIdFor(product)
          && product?.identity?.pieceId
        );
        return result(this, product, complete ? 'PASS' : 'FAIL',
          complete ? 'IDENTITY_COMPLETE' : 'IDENTITY_INCOMPLETE',
          evidence('identity', complete));
      }
    },
    {
      id: 'build.product.exact_piece',
      enforcement: 'HARD',
      evaluate(product) {
        const exact = product?.classification?.exactPiece === true;
        return result(this, product, exact ? 'PASS' : 'FAIL',
          exact ? 'EXACT_PIECE' : 'NOT_EXACT_PIECE',
          evidence('classification.exactPiece', exact));
      }
    },
    {
      id: 'build.product.normalized_validation',
      enforcement: 'HARD',
      evaluate(product) {
        const valid = product?.validation?.valid === true;
        return result(this, product, valid ? 'PASS' : 'FAIL',
          valid ? 'NORMALIZED_VALID' : 'NORMALIZED_INVALID',
          evidence('validation.valid', valid));
      }
    },
    {
      id: 'build.measurements.complete',
      enforcement: 'HARD',
      evaluate(product) {
        const measurements = product?.measurements || {};
        const complete = [
          measurements.weight,
          measurements.maximumLength,
          measurements.maximumWidth,
          measurements.maximumHeight
        ].every(presentMeasurement);
        return result(this, product, complete ? 'PASS' : 'FAIL',
          complete ? 'MEASUREMENTS_COMPLETE' : 'MEASUREMENTS_INCOMPLETE',
          evidence('measurements.required', complete));
      }
    },
    {
      id: 'build.photography.complete',
      enforcement: 'HARD',
      evaluate(product) {
        const hero = product?.media?.heroImage;
        const complete = Boolean(hero?.url || hero?.previewUrl);
        return result(this, product, complete ? 'PASS' : 'FAIL',
          complete ? 'PHOTOGRAPHY_COMPLETE' : 'PHOTOGRAPHY_INCOMPLETE',
          evidence('media.heroImage', complete));
      }
    },
    {
      id: 'build.inventory.public_state',
      enforcement: 'HARD',
      evaluate(product) {
        const state = String(product?.availability?.state || '').toLowerCase();
        const inventoryStatus = String(product?.availability?.inventoryStatus || '').toUpperCase();
        const publicationStatus = String(
          product?.publication?.status || product?.shopifyStatus || 'ACTIVE'
        ).toUpperCase();
        const futureWave = product?.storefrontEligibility?.futureWave === true
          || product?.release?.futureWave === true;
        const reserved = inventoryStatus === 'RESERVED';
        const sold = state === 'sold' || inventoryStatus === 'SOLD';
        const draft = publicationStatus === 'DRAFT';
        const allowed = !futureWave && !reserved && !sold && !draft;
        const code = futureWave
          ? 'FUTURE_WAVE'
          : reserved
            ? 'RESERVED'
            : sold
              ? 'SOLD'
              : draft
                ? 'DRAFT'
                : 'PUBLIC_STATE_ALLOWED';
        return result(this, product, allowed ? 'PASS' : 'FAIL', code, [
          ...evidence('availability.state', state),
          ...evidence('availability.inventoryStatus', inventoryStatus),
          ...evidence('publication.status', publicationStatus)
        ]);
      }
    },
    {
      id: 'build.inventory.c3_projection',
      enforcement: 'HARD',
      evaluate(product) {
        const projection = product?.storefrontEligibility;
        if (!projection) {
          return result(this, product, 'NOT_APPLICABLE', 'C3_NOT_CONNECTED', []);
        }
        const eligible = projection.eligible === true && projection.state === 'LIVE';
        return result(this, product, eligible ? 'PASS' : 'FAIL',
          eligible ? 'C3_PUBLIC_ELIGIBLE' : 'C3_PUBLIC_INELIGIBLE',
          evidence('storefrontEligibility', {
            eligible: projection.eligible,
            state: projection.state,
            version: projection.version
          }, 'c3_public_projection'));
      }
    },
    {
      id: 'build.inventory.shopify_sellable',
      enforcement: 'HARD',
      evaluate(product) {
        const sellable = product?.availability?.sellable === true
          && product?.availability?.productAvailable !== false
          && product?.availability?.variantAvailable !== false
          && Number(product?.availability?.inventoryQuantity) !== 0;
        return result(this, product, sellable ? 'PASS' : 'FAIL',
          sellable ? 'SHOPIFY_SELLABLE' : 'SHOPIFY_UNAVAILABLE',
          evidence('availability.sellable', sellable, 'shopify_current_validation'));
      }
    },
    {
      id: 'build.budget.within_remaining',
      enforcement: 'HARD',
      evaluate(product, context) {
        const price = Number(product?.commerce?.priceCents);
        const remaining = Number(context.remainingBudgetCents);
        const pass = Number.isFinite(price)
          && price > 0
          && (!Number.isFinite(remaining) || price <= remaining);
        return result(this, product, pass ? 'PASS' : 'FAIL',
          pass ? 'WITHIN_REMAINING_BUDGET' : 'OVER_REMAINING_BUDGET', [
            ...evidence('commerce.priceCents', price),
            ...evidence('budget.remainingCents', Number.isFinite(remaining) ? remaining : null, 'build_studio')
          ]);
      }
    },
    {
      id: 'build.history.not_selected',
      enforcement: 'HARD',
      evaluate(product, context) {
        const variantId = variantIdFor(product);
        const selected = context.selectedVariantIds.has(variantId);
        return result(this, product, selected ? 'FAIL' : 'PASS',
          selected ? 'ALREADY_ACCEPTED' : 'NOT_ALREADY_ACCEPTED',
          evidence('history.selected', selected, 'build_studio'));
      }
    },
    {
      id: 'build.history.not_rejected',
      enforcement: 'HARD',
      evaluate(product, context) {
        const variantId = variantIdFor(product);
        const rejected = context.rejectedVariantIds.has(variantId);
        return result(this, product, rejected ? 'FAIL' : 'PASS',
          rejected ? 'PREVIOUSLY_REJECTED' : 'NOT_PREVIOUSLY_REJECTED',
          evidence('history.rejected', rejected, 'build_studio'));
      }
    },
    {
      id: 'build.history.not_previously_shown',
      enforcement: 'HARD',
      evaluate(product, context) {
        const variantId = variantIdFor(product);
        const shown = context.shownVariantIds.has(variantId)
          && context.allowCurrentVariantId !== variantId;
        return result(this, product, shown ? 'FAIL' : 'PASS',
          shown ? 'PREVIOUSLY_SHOWN' : 'NOT_PREVIOUSLY_SHOWN',
          evidence('history.shown', shown, 'build_studio'));
      }
    }
  ];
  const dynamicInventoryRuleIds = new Set([
    'build.identity.complete',
    'build.product.exact_piece',
    'build.product.normalized_validation',
    'build.measurements.complete',
    'build.photography.complete',
    'build.inventory.public_state',
    'build.inventory.c3_projection',
    'build.inventory.shopify_sellable'
  ]);
  const dynamicInventoryRules = buildRules.filter((rule) => dynamicInventoryRuleIds.has(rule.id));

  const smartBoxResult = (rule, outcome, reasonCode, evidenceItems = []) => ({
    rule_id: rule.id,
    rule_version: RULE_VERSION,
    subject: { type: 'current_shopify_build' },
    outcome,
    enforcement: rule.enforcement,
    reason_code: reasonCode,
    evidence: evidenceItems,
    explanation_code: reasonCode
  });

  const smartBoxRules = [
    {
      id: 'smart_box.cart.shopify_snapshot',
      enforcement: 'HARD',
      evaluate(facts) {
        const cart = facts?.shopifyCart;
        const valid = Boolean(
          cart
          && Array.isArray(cart.exactPieces)
          && typeof cart.currency === 'string'
          && Number.isFinite(Number(cart.merchandiseSubtotalCents))
        );
        return smartBoxResult(this, valid ? 'PASS' : 'FAIL',
          valid ? 'SHOPIFY_CART_SNAPSHOT_VALID' : 'SHOPIFY_CART_SNAPSHOT_INVALID',
          evidence('shopifyCart.valid', valid, 'shopify_current_validation'));
      }
    },
    {
      id: 'smart_box.cart.authority',
      enforcement: 'HARD',
      evaluate(facts) {
        const authority = String(facts?.cartAuthority || '');
        const valid = authority === 'shopify';
        return smartBoxResult(this, valid ? 'PASS' : 'FAIL',
          valid ? 'SHOPIFY_CART_AUTHORITATIVE' : 'DUPLICATE_CART_AUTHORITY',
          evidence('cartAuthority', authority, 'shopify_current_validation'));
      }
    },
    {
      id: 'smart_box.build.brief_context',
      enforcement: 'INFORMATIONAL',
      evaluate(facts) {
        const brief = facts?.buildBrief || {};
        const complete = Boolean(
          brief.project
          && brief.style
          && brief.budget
          && Number(brief.enclosure?.width) > 0
          && Number(brief.enclosure?.depth) > 0
          && Number(brief.enclosure?.height) > 0
        );
        return smartBoxResult(this, complete ? 'PASS' : 'UNKNOWN',
          complete ? 'BUILD_BRIEF_AVAILABLE' : 'BUILD_BRIEF_INCOMPLETE',
          evidence('buildBrief.complete', complete, 'build_studio'));
      }
    },
    {
      id: 'smart_box.shipping.observation_only',
      enforcement: 'HARD',
      evaluate(facts) {
        const plan = facts?.currentShippingPlan;
        if (!plan) return smartBoxResult(this, 'NOT_APPLICABLE', 'SHIPPING_PLAN_NOT_AVAILABLE');
        const observed = plan.source === 'existing_smart_box';
        return smartBoxResult(this, observed ? 'PASS' : 'FAIL',
          observed ? 'EXISTING_SHIPPING_PLAN_OBSERVED' : 'SHIPPING_PLAN_SOURCE_UNAUTHORIZED',
          evidence('currentShippingPlan.source', plan.source || null, 'shopify_current_validation'));
      }
    },
    {
      id: 'smart_box.compatibility.disabled',
      enforcement: 'INFORMATIONAL',
      evaluate() {
        return smartBoxResult(this, 'NOT_APPLICABLE', 'COMPATIBILITY_RULES_DISABLED');
      }
    },
    {
      id: 'smart_box.packing.no_recalculation',
      enforcement: 'HARD',
      evaluate(facts) {
        const recalculated = facts?.packingRecalculated === true;
        return smartBoxResult(this, recalculated ? 'FAIL' : 'PASS',
          recalculated ? 'PACKING_RECALCULATION_FORBIDDEN' : 'PACKING_RESULT_OBSERVED_ONLY');
      }
    }
  ];

  const searchToken = (value) => String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  const searchList = (value) => Array.isArray(value) ? value.filter(Boolean) : [];
  const searchMeasurement = (product, field) => {
    const measurements = product?.measurements || {};
    if (field === 'length') return measurements.maximumLength;
    if (field === 'width') return measurements.maximumWidth;
    if (field === 'height') return measurements.maximumHeight;
    if (field === 'thickness') return measurements.thickness;
    if (field === 'diameter') return measurements.outerDiameter || measurements.maximumWidth;
    if (field === 'opening') {
      return measurements.openingDiameter
        || measurements.openingWidth
        || measurements.openingHeight;
    }
    if (field === 'weight') return measurements.weight;
    return null;
  };
  const searchRangePass = (value, range = {}) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return false;
    if (range.min !== null && range.min !== undefined
      && Number.isFinite(Number(range.min)) && numeric < Number(range.min)) return false;
    if (range.max !== null && range.max !== undefined
      && Number.isFinite(Number(range.max)) && numeric > Number(range.max)) return false;
    return true;
  };
  const searchShapeTokens = (product) => new Set([
    ...searchList(product?.merchandising?.searchKeywords),
    ...searchList(product?.classification?.suggestedUses),
    ...searchList(product?.compatibility?.buildRoles),
    product?.classification?.family === 'cork_round' || product?.classification?.family === 'half_round'
      ? 'hollow'
      : '',
    product?.classification?.family === 'cork_flat' ? 'flat' : '',
    product?.classification?.family === 'branch' ? 'branching' : '',
    Number(product?.measurements?.curvature) > 0 ? 'curved' : ''
  ].map(searchToken).filter(Boolean));
  const searchCriteriaResult = (rule, product, active, pass, reasonCode, evidenceItems = []) =>
    result(
      rule,
      product,
      active ? (pass ? 'PASS' : 'FAIL') : 'NOT_APPLICABLE',
      active ? reasonCode : `${rule.id.toUpperCase().replace(/[^A-Z0-9]+/g, '_')}_NOT_REQUESTED`,
      evidenceItems
    );

  const searchRules = [
    ...dynamicInventoryRules,
    {
      id: 'search.classification.requested',
      enforcement: 'HARD',
      evaluate(product, context) {
        const intent = context.intent || {};
        const active = Boolean(intent.family || intent.size || intent.shape);
        const familyPass = !intent.family
          || searchToken(product?.classification?.family) === searchToken(intent.family);
        const sizePass = !intent.size
          || searchToken(product?.classification?.sizeClass) === searchToken(intent.size);
        const shapePass = !intent.shape || searchShapeTokens(product).has(searchToken(intent.shape));
        return searchCriteriaResult(this, product, active, familyPass && sizePass && shapePass,
          familyPass && sizePass && shapePass ? 'SEARCH_CLASSIFICATION_MATCH' : 'SEARCH_CLASSIFICATION_MISMATCH', [
            ...evidence('classification.family', product?.classification?.family || null),
            ...evidence('classification.sizeClass', product?.classification?.sizeClass || null),
            ...evidence('search.shapeTokens', [...searchShapeTokens(product)])
          ]);
      }
    },
    {
      id: 'search.price.requested',
      enforcement: 'HARD',
      evaluate(product, context) {
        const price = context.intent?.price;
        const active = Boolean(price);
        const value = Number(product?.commerce?.priceCents);
        const range = {
          min: Number.isFinite(Number(price?.min_cents)) ? Number(price.min_cents) : null,
          max: Number.isFinite(Number(price?.max_cents)) ? Number(price.max_cents) : null
        };
        const pass = searchRangePass(value, range);
        return searchCriteriaResult(this, product, active, pass,
          pass ? 'SEARCH_PRICE_MATCH' : 'SEARCH_PRICE_MISMATCH',
          evidence('commerce.priceCents', value));
      }
    },
    {
      id: 'search.measurements.requested',
      enforcement: 'HARD',
      evaluate(product, context) {
        const requested = context.intent?.measurements || {};
        const entries = Object.entries(requested);
        const active = entries.length > 0;
        const observations = entries.map(([field, range]) => {
          const measurement = searchMeasurement(product, field);
          return {
            field,
            value: Number(measurement?.value),
            unit: measurement?.unit || null,
            pass: searchRangePass(measurement?.value, range)
          };
        });
        const pass = observations.every((entry) => entry.pass);
        return searchCriteriaResult(this, product, active, pass,
          pass ? 'SEARCH_MEASUREMENTS_MATCH' : 'SEARCH_MEASUREMENTS_MISMATCH',
          evidence('measurements.requested', observations));
      }
    },
    {
      id: 'search.role_flags_wave.requested',
      enforcement: 'HARD',
      evaluate(product, context) {
        const intent = context.intent || {};
        const active = Boolean(intent.role || intent.wave || searchList(intent.flags).length);
        const roles = searchList(product?.compatibility?.buildRoles).map(searchToken);
        const requiredRoles = searchList(intent.role_public_values).map(searchToken);
        const rolePass = !intent.role || requiredRoles.some((role) => roles.includes(role));
        const wavePass = !intent.wave
          || searchToken(product?.release?.wave) === searchToken(intent.wave)
          || searchToken(product?.classification?.importBatch) === searchToken(intent.wave);
        const flags = new Set(searchList(intent.flags));
        const flagPass = (!flags.has('statement_piece') || product?.classification?.statementPiece === true)
          && (!flags.has('new_arrival') || product?.classification?.newArrival === true)
          && (!flags.has('available_now') || product?.availability?.sellable === true);
        const pass = rolePass && wavePass && flagPass;
        return searchCriteriaResult(this, product, active, pass,
          pass ? 'SEARCH_ROLE_FLAGS_MATCH' : 'SEARCH_ROLE_FLAGS_MISMATCH', [
            ...evidence('compatibility.buildRoles', roles),
            ...evidence('classification.flags', {
              statementPiece: product?.classification?.statementPiece === true,
              newArrival: product?.classification?.newArrival === true
            })
          ]);
      }
    },
    {
      id: 'search.compatibility.context_only',
      enforcement: 'INFORMATIONAL',
      evaluate(product, context) {
        const hasContext = Boolean(context.intent?.animal || context.intent?.enclosure);
        return result(this, product, hasContext ? 'UNKNOWN' : 'NOT_APPLICABLE',
          hasContext ? 'SEARCH_COMPATIBILITY_CONTEXT_NOT_SCORED' : 'SEARCH_COMPATIBILITY_NOT_REQUESTED',
          hasContext
            ? evidence('search.compatibilityAuthority', 'disabled_unapproved', 'approved_configuration')
            : []);
      }
    }
  ];

  const responseForResults = (request, results) => ({
    evaluation_id: request.evaluation_id || '',
    rule_set_id: request.rule_set_id,
    rule_set_version: request.rule_set_version || RULE_VERSION,
    results,
    hard_failures: results
      .filter((entry) => entry.enforcement === 'HARD' && entry.outcome === 'FAIL')
      .map((entry) => `${entry.subject.variant_id || entry.subject.type}:${entry.reason_code}`),
    unknown_required_facts: results
      .filter((entry) => entry.enforcement === 'HARD' && entry.outcome === 'UNKNOWN')
      .map((entry) => `${entry.subject.variant_id || entry.subject.type}:${entry.reason_code}`),
    evidence_digest: results
      .map((entry) => `${entry.subject.variant_id || entry.subject.type}:${entry.rule_id}:${entry.outcome}`)
      .join('|')
  });

  const evaluate = (request = {}) => {
    if (request.rule_set_id === 'smart_box_advisor_v1') {
      return responseForResults(
        request,
        smartBoxRules.map((rule) => rule.evaluate(request.facts || {}))
      );
    }

    if (request.rule_set_id === 'dynamic_inventory_v1') {
      const results = (request.subjects || []).flatMap((product) =>
        dynamicInventoryRules.map((rule) => rule.evaluate(product, {}))
      );
      return responseForResults(request, results);
    }

    if (request.rule_set_id === 'search_advisor_v1') {
      const context = { intent: request.facts?.intent || {} };
      const results = (request.subjects || []).flatMap((product) =>
        searchRules.map((rule) => rule.evaluate(product, context))
      );
      return responseForResults(request, results);
    }

    if (request.rule_set_id !== 'build_advisor_v1') {
      return {
        evaluation_id: request.evaluation_id || '',
        rule_set_id: request.rule_set_id || '',
        rule_set_version: request.rule_set_version || '',
        results: [],
        hard_failures: ['UNKNOWN_RULE_SET'],
        unknown_required_facts: [],
        evidence_digest: 'unknown-rule-set'
      };
    }

    const context = {
      remainingBudgetCents: request.facts?.remainingBudgetCents,
      selectedVariantIds: new Set((request.facts?.selectedVariantIds || []).map(String)),
      rejectedVariantIds: new Set((request.facts?.rejectedVariantIds || []).map(String)),
      shownVariantIds: new Set((request.facts?.shownVariantIds || []).map(String)),
      allowCurrentVariantId: String(request.facts?.allowCurrentVariantId || '')
    };
    const results = (request.subjects || []).flatMap((product) =>
      buildRules.map((rule) => rule.evaluate(product, context))
    );

    return responseForResults(request, results);
  };

  return Object.freeze({
    RULE_VERSION,
    ruleIds: Object.freeze(buildRules.map((rule) => rule.id)),
    ruleSets: Object.freeze({
      build_advisor_v1: Object.freeze(buildRules.map((rule) => rule.id)),
      smart_box_advisor_v1: Object.freeze(smartBoxRules.map((rule) => rule.id)),
      dynamic_inventory_v1: Object.freeze(dynamicInventoryRules.map((rule) => rule.id)),
      search_advisor_v1: Object.freeze(searchRules.map((rule) => rule.id))
    }),
    evaluate,
    variantIdFor
  });
});
